import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Wrench, Lightbulb, ListChecks, Search, Sparkles } from "lucide-react";
import { getFaq, getFaqs } from "@/lib/api";
import { FaqBadges } from "@/components/FaqBadges";
import { VotePanel } from "@/components/VotePanel";
import { FaqCard } from "@/components/FaqCard";
import { sized, srcSet } from "@/lib/img";
import { StepList } from "@/components/StepList";

const Block = ({ icon: Icon, title, children, testId }) => (
  <section className="bg-[#13171F] border border-[#27303E] rounded-sm p-5 sm:p-6 lg:p-8" data-testid={testId}>
    <h2 className="flex items-center gap-2 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight mb-4 sm:mb-5">
      <Icon size={20} className="text-amber-500" /> {title}
    </h2>
    {children}
  </section>
);

export default function FaqDetail() {
  const { slug } = useParams();
  const qc = useQueryClient();
  const { data: faq, isLoading, isError } = useQuery({ queryKey: ["faq", slug], queryFn: () => getFaq(slug) });
  const { data: related = [] } = useQuery({
    queryKey: ["faqs", faq?.category, ""],
    queryFn: () => getFaqs({ category: faq.category }),
    enabled: !!faq,
  });

  if (isLoading) return <div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-amber-500" /></div>;
  if (isError) return <p className="py-32 text-center text-slate-400" data-testid="faq-not-found">Question not found.</p>;

  return (
    <main data-testid="faq-detail-page">
      <div className="relative h-[40vh] sm:h-[46vh] min-h-[300px] sm:min-h-[340px] overflow-hidden border-b border-[#27303E]">
        <img src={sized(faq.image, 1600)} srcSet={srcSet(faq.image)} sizes="100vw" alt={faq.question} fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" data-testid="faq-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] via-[#0B0D10]/60 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-6 sm:pb-10">
          <Link to={`/faqs?category=${faq.category}`} data-testid="back-to-category-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-amber-500 hover:text-amber-300 mb-3 sm:mb-4 py-1 w-fit">
            <ArrowLeft size={14} /> Back to category
          </Link>
          <h1 className="font-display text-[2rem] sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95]" data-testid="faq-question">{faq.question}</h1>
          <div className="mt-4 sm:mt-5"><FaqBadges faq={faq} /></div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-5 sm:space-y-6">
        <div className="border-l-4 border-amber-500 pl-4 sm:pl-6 py-1 sm:py-2">
          <p className="font-mono text-xs uppercase tracking-widest text-amber-500 mb-2">Quick answer</p>
          <p className="text-[17px] md:text-xl text-slate-100 leading-relaxed" data-testid="faq-summary">{faq.summary}</p>
        </div>
        <div className="space-y-4 text-[15px] sm:text-base text-slate-300 leading-relaxed" data-testid="faq-answer">
          {faq.answer.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
          <Block icon={Search} title="Common causes" testId="faq-causes">
            <ul className="space-y-3">
              {faq.causes.map((c) => (
                <li key={c} className="flex gap-3 text-slate-300"><span className="mt-2 w-1.5 h-1.5 bg-amber-500 shrink-0" />{c}</li>
              ))}
            </ul>
          </Block>
          <Block icon={Wrench} title="Tools needed" testId="faq-tools">
            <div className="flex flex-wrap gap-2">
              {faq.tools.map((t) => <span key={t} className="font-mono text-xs border border-[#27303E] bg-[#0B0D10] px-3 py-2 rounded-sm">{t}</span>)}
            </div>
          </Block>
        </div>

        <Block icon={ListChecks} title="Diagnose & fix: step by step" testId="faq-steps">
          <StepList steps={faq.steps} images={faq.step_images} />
        </Block>

        {faq.tip && (
          <div className="flex gap-4 bg-amber-500/10 border border-amber-500/40 rounded-sm p-4 sm:p-5" data-testid="faq-tip">
            <Lightbulb className="text-amber-400 shrink-0" />
            <p className="text-amber-100"><span className="font-bold uppercase font-display tracking-wide mr-2">Pro tip:</span>{faq.tip}</p>
          </div>
        )}

        <VotePanel faq={faq} onVoted={(u) => { qc.setQueryData(["faq", slug], u); qc.invalidateQueries({ queryKey: ["faqs"] }); }} />

        <Link to={`/ask?q=${encodeURIComponent(faq.question)}`} data-testid="ask-followup-btn" className="flex items-center justify-between gap-4 border border-[#27303E] hover:border-amber-500 active:bg-[#13171F] rounded-sm p-5 sm:p-6 transition-colors duration-200 group">
          <span>
            <span className="font-display text-lg sm:text-xl font-bold uppercase block">Still stuck? Ask a follow-up</span>
            <span className="text-sm text-slate-400">Get advice specific to your vehicle's year, make and model.</span>
          </span>
          <Sparkles className="text-amber-500 transition-transform duration-300 group-hover:rotate-12" />
        </Link>

        {related.filter((r) => r.slug !== slug).length > 0 && (
          <section className="pt-6 sm:pt-10">
            <h2 className="font-display text-[1.7rem] sm:text-3xl font-extrabold uppercase tracking-tight mb-4 sm:mb-6">Related questions</h2>
            <div className="grid gap-3 sm:gap-5" data-testid="related-faqs">
              {related.filter((r) => r.slug !== slug).map((r, i) => <FaqCard key={r.slug} faq={r} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
